/**
 * The portal's API client.
 *
 * Every call carries the signed-in user's Supabase token, which the Worker
 * verifies and then forwards to Postgres so RLS decides what comes back. The
 * portal is not the authorisation layer and does not pretend to be: a request
 * for something the caller cannot see returns not-found from the database, not
 * from a check in this file.
 */

import type {
  ElementContext,
  PageContext,
  TicketPriority,
  TicketType,
  WorkflowState,
} from '@relay/shared/types';

import { accessToken, endExpiredSession, refreshedAccessToken } from './supabase.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Is this worth retrying, or is it the caller's fault? */
  get retryable(): boolean {
    return this.status >= 500 || this.status === 429;
  }
}

const EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

/**
 * Sign out, and say why, for a session that cannot be saved.
 *
 * Without the sign-out the portal stayed "signed in" with every query failing:
 * the shell drew, each panel showed its own error, and nothing offered the one
 * thing that would help.
 */
async function expired(reason: string): Promise<never> {
  console.warn('[relay] session ended', { reason });
  await endExpiredSession();
  throw new ApiError(401, 'unauthenticated', EXPIRED_MESSAGE, { reason });
}

function send(path: string, init: RequestInit, token: string): Promise<Response> {
  return fetch(`/api${path}`, {
    ...init,
    // Not followed. The only redirect `/api` gives a fetch is Cloudflare Access
    // sending an expired login to its own sign-in page, on another origin —
    // which a followed fetch reports as a network failure, indistinguishable
    // from the API being down. Unfollowed, it is an `opaqueredirect` we can
    // recognise.
    redirect: 'manual',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
      ...(init.headers ?? {}),
    },
  });
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await accessToken();
  if (token === null) return expired('no_session');

  let response = await send(path, init, token);

  // The token went stale without the background refresh noticing. One forced
  // refresh and one retry, so that costs a second rather than the session.
  if (response.status === 401) {
    const fresh = await refreshedAccessToken();
    if (fresh === null) return expired('refresh_failed');
    response = await send(path, init, fresh);
    if (response.status === 401) return expired('rejected_after_refresh');
  }

  if (response.type === 'opaqueredirect') return expired('access_login_expired');

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body: unknown = text.length === 0 ? {} : JSON.parse(text);

  if (!response.ok) {
    const error = body as { error?: string; message?: string; detail?: unknown; requestId?: string };
    throw new ApiError(
      response.status,
      error.error ?? 'request_failed',
      error.message ?? 'Something went wrong.',
      error.detail,
      error.requestId,
    );
  }

  return body as T;
}

const get = <T,>(path: string): Promise<T> => call<T>(path);
const post = <T,>(path: string, body?: unknown): Promise<T> =>
  call<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
const patch = <T,>(path: string, body: unknown): Promise<T> =>
  call<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const put = <T,>(path: string, body: unknown): Promise<T> =>
  call<T>(path, { method: 'PUT', body: JSON.stringify(body) });
const remove = (path: string): Promise<void> => call<void>(path, { method: 'DELETE' });

/* -------------------------------------------------------------------------- */
/* Types the portal renders                                                   */
/* -------------------------------------------------------------------------- */

export type Me = {
  readonly userId: string;
  readonly email: string;
  readonly globalRole: 'admin' | 'triager' | 'read_only' | 'client_viewer';
  readonly projectIds: readonly string[];
  readonly projectRoles: Readonly<Record<string, string>>;
};

export type ConnectionStatusView = {
  readonly widget: { status: string; detail: string | null; lastSeenAt: string | null };
};

export type ProjectSummary = {
  readonly id: string;
  readonly slug: string;
  readonly displayName: string;
  readonly clientName: string;
  readonly ticketPrefix: string;
  readonly ticketCount: number;
  readonly repoConnected: boolean;
  readonly agentEnabled: boolean;
  /** Set when the project is archived: finished, hidden, entirely intact. */
  readonly archivedAt: string | null;
  /**
   * The project's sites, names only, for the sidebar.
   *
   * Comes from `navigable_sites` rather than the environments table, which a
   * triager cannot read — it carries the signing secret. So there is no key
   * and no origin list here, on purpose.
   */
  readonly sites: readonly { readonly id: string; readonly label: string; readonly kind: string }[];
  readonly connectionStatus: ConnectionStatusView;
};

/** A client business, and the domains approved into it. */
export type ClientTeam = {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly domains: readonly string[];
};

export type ClientDomainStatus = 'pending' | 'approved' | 'denied';

/** A business domain a verified client has arrived from, and what was decided about it. */
export type ClientDomain = {
  readonly domain: string;
  readonly status: ClientDomainStatus;
  readonly teamId: string | null;
  readonly teamName: string | null;
  /** Verified people at this domain now. */
  readonly people: number;
  readonly firstSeenAt: string;
  readonly decidedAt: string | null;
};

export type ClientDomainPerson = { readonly name: string; readonly email: string; readonly verifiedAt: string };

export type ClientDomainDecision =
  | { readonly decision: 'approve'; readonly teamId: string }
  | { readonly decision: 'approve'; readonly newTeamName: string }
  | { readonly decision: 'deny' }
  | { readonly decision: 'pending' };

/** One of a project's sites, as the portal lists and edits them. */
export type Site = {
  readonly id: string;
  readonly kind: 'production' | 'staging' | 'local';
  readonly label: string;
  readonly repo: string | null;
  readonly ownerSlackNames: readonly string[];
  readonly slackChannel: string | null;
  readonly publicKey: string;
  readonly previousPublicKey: string | null;
  readonly previousKeyExpiresAt: string | null;
  readonly originAllowlist: readonly string[];
  readonly lastSeenAt: string | null;
  readonly signingSecretSet: boolean;
};

export type TicketSummary = {
  readonly id: string;
  readonly ticketKey: string;
  readonly projectId: string;
  readonly source: 'client_widget' | 'internal';
  readonly type: TicketType;
  readonly title: string;
  readonly priority: TicketPriority;
  readonly workflowState: WorkflowState;
  readonly boardPosition: string;
  readonly assigneeKind: 'user' | 'agent' | 'unassigned';
  readonly assigneeUserId: string | null;
  readonly identityVerified: boolean;
  readonly duplicateOfTicketId: string | null;
  /** The Jira ticket this became, or null until the triage bot files one. */
  readonly externalKey: string | null;
  readonly externalUrl: string | null;
  readonly externalStatus: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type BoardCard = {
  readonly id: string;
  readonly ticketKey: string;
  readonly title: string;
  readonly type: TicketType;
  readonly priority: TicketPriority;
  readonly boardPosition: string;
  readonly assigneeKind: 'user' | 'agent' | 'unassigned';
  readonly assigneeUserId: string | null;
  readonly source: 'client_widget' | 'internal';
  readonly updatedAt: string;
  readonly commentCount: number;
  readonly attachmentCount: number;
  /**
   * The site this was reported on, or null for a ticket we raised ourselves.
   *
   * A project is a customer and a customer has several sites, so the card has
   * to say which one broke. Null is not missing data — an internal ticket was
   * not reported on a site at all.
   */
  readonly siteId: string | null;
  readonly siteLabel: string | null;
  /**
   * Blast radius, 1 to 100, when the bot has assessed it.
   *
   * On the card because it changes which ticket you pick up first. Confidence
   * is not: it is a judgement about a judgement, worth reading once you care
   * about a ticket and noise on forty of them at once.
   */
  readonly triageRisk: number | null;
};

export type Board = {
  readonly columns: readonly { readonly state: WorkflowState; readonly tickets: readonly BoardCard[] }[];
  readonly workflow: {
    readonly states: readonly WorkflowState[];
    readonly transitions: Readonly<Record<string, readonly WorkflowState[]>>;
    readonly initialState: WorkflowState;
  };
  /** The site the server filtered to, echoed back so the UI cannot disagree. */
  readonly site: string | null;
  /** The sites represented on this board, for the filter to offer. */
  readonly sites: readonly { readonly id: string; readonly label: string }[];
};

/**
 * One of the people named on a ticket.
 *
 * `email` is null for a client reading their own ticket: they need our names
 * to follow a conversation and have no business learning our addresses.
 */
export type Person = {
  readonly displayName: string;
  readonly email: string | null;
  readonly isClient: boolean;
};

export type TicketDetail = {
  readonly ticket: TicketSummary & {
    readonly fields: {
      readonly problemStatement: string | null;
      readonly currentBehaviour: string | null;
      readonly expectedBehaviour: string | null;
      readonly desiredOutcome: string | null;
      readonly reproSteps: readonly string[];
      readonly acceptanceCriteria: readonly { readonly text: string; readonly done: boolean }[];
    };
    readonly priorityRationale: string | null;
    readonly aiGuessedFields: readonly string[];
    readonly buildSha: string | null;
    /**
     * Where a blocked ticket came from, so it can say so and go back there.
     *
     * Blocking is a holding state rather than a step, and "Blocked" on its own
     * loses the only thing that says what unblocking means.
     */
    readonly stateBeforeBlock: WorkflowState | null;
    readonly rejectionReason: string | null;
    readonly submissionId: string | null;
    readonly createdBy: string | null;
    /**
     * The client account that reported it, if one was signed in.
     *
     * Not the same question as `identityVerified` on the summary, and the two
     * get confused constantly: this is an account of ours that signed in,
     * that one is whether the host application also signed an assertion about
     * who they are. Most sites do not, so most real reporters have this set
     * and that unset.
     */
    readonly submittedByUserId: string | null;
    readonly sharedWithAllClients: boolean;
    readonly triageState: string;
    readonly triageRecommendation: string | null;
    readonly triageRationale: string | null;
    /** How sure the bot is of its own recommendation, 0 to 1. */
    readonly triageConfidence: number | null;
    /** Blast radius, 1 to 100. Not effort, and not the same as confidence. */
    readonly triageRisk: number | null;
    readonly triageAnalysedAt: string | null;
    /**
     * The tracker ticket this report became — staff-facing only.
     *
     * Null until the triage bot files one, and null forever on a report closed
     * without a ticket, so every read of these is conditional. The
     * client-facing response never carries them: `externalUrl` would tell a
     * customer our Atlassian tenant, and `externalStatus` is engineering's own
     * wording rather than the vocabulary they are shown.
     */
    readonly externalSystem: string | null;
    readonly externalKey: string | null;
    readonly externalUrl: string | null;
    readonly externalStatus: string | null;
  };
  readonly project: {
    readonly id: string;
    readonly slug: string;
    readonly displayName: string;
    readonly workflow: Board['workflow'];
    readonly agentAssignmentAllowed: boolean;
    /**
     * Whether the widget is even collecting this.
     *
     * Needed to tell "nothing went wrong" from "we were not looking", which
     * an empty log list cannot say on its own.
     */
    readonly capture: { readonly console: boolean; readonly network: boolean };
  };
  readonly submission: {
    readonly rawDescription: string;
    // Typed rather than a bag of unknowns: the ticket page renders these
    // fields directly, and an untyped value is how `[object Object]` reaches a
    // screen.
    readonly pageContext: PageContext;
    readonly elementContext: ElementContext | null;
    readonly consoleEntries: readonly { level: string; message: string; at: string }[];
    readonly networkEntries: readonly {
      method: string;
      urlPath: string;
      status: number | null;
      durationMs: number | null;
      at: string;
    }[];
    readonly identity: { verified: boolean; email: string | null; displayName: string | null };
    readonly screenshotMethod: string;
    readonly clarifyingAnswers: readonly { question: string; answer: string }[];
    readonly createdAt: string;
  } | null;
  readonly attachments: readonly {
    readonly id: string;
    readonly kind: string;
    readonly url: string;
    readonly redacted: boolean;
    readonly autoBlurRegions: number;
    readonly capturedWith: string;
  }[];
  readonly events: readonly {
    readonly id: string;
    readonly kind: string;
    readonly actorUserId: string | null;
    readonly actorKind: string;
    readonly payload: Record<string, unknown>;
    readonly createdAt: string;
  }[];
  readonly comments: readonly {
    readonly id: string;
    readonly authorUserId: string | null;
    readonly body: string;
    readonly clientVisible: boolean;
    readonly createdAt: string;
    readonly updatedAt: string;
  }[];
  readonly links: readonly Record<string, unknown>[];
  /**
   * Who the ids on this ticket belong to, keyed by user id.
   *
   * A map rather than a name repeated on every comment: the same person writes
   * most of them, and inlining would grow the response without carrying more.
   */
  readonly people: Readonly<Record<string, Person>>;
};

export type ProjectHealth = {
  readonly uploads: { readonly abandonedToday: number };
  /** Named problems, decided server-side so the judgement lives in one place. */
  readonly concerns: readonly string[];
};

export type TicketQuery = {
  projectIds?: readonly string[];
  types?: readonly TicketType[];
  states?: readonly WorkflowState[];
  priorities?: readonly TicketPriority[];
  sources?: readonly ('client_widget' | 'internal')[];
  assigneeKind?: 'user' | 'agent' | 'unassigned';
  assigneeUserId?: string;
  olderThanDays?: number;
  hasDuplicates?: boolean;
  search?: string;
  sort?: 'created_desc' | 'created_asc' | 'priority' | 'updated_desc' | 'board';
  limit?: number;
  cursor?: string;
};

/**
 * The counts behind the queue's triage lanes.
 *
 * Each lane carries how much is waiting and how long the oldest of it has
 * been: a count on its own reads the same on the first bad day as on the
 * tenth, and the age is what makes the lane worth looking at.
 */
export type QueueLane = {
  readonly count: number;
  readonly oldestCreatedAt: string | null;
};

export type QueueSummary = {
  readonly needsTriage: QueueLane;
  readonly readyUnassigned: QueueLane & { readonly urgentOrHigh: number };
  readonly blocked: QueueLane;
  readonly stale: QueueLane & { readonly afterDays: number };
};

export type Member = {
  readonly userId: string;
  readonly role: string;
  readonly email: string;
  readonly displayName: string;
  readonly active: boolean;
};

/* -------------------------------------------------------------------------- */
/* Endpoints                                                                  */
/* -------------------------------------------------------------------------- */

export const api = {
  me: (): Promise<Me> => get<Me>('/me'),

  projects: (options?: { archived?: boolean }): Promise<{ projects: readonly ProjectSummary[] }> =>
    get<{ projects: readonly ProjectSummary[] }>(
      options?.archived === true ? '/projects?archived=true' : '/projects',
    ),

  projectStatus: (
    projectId: string,
  ): Promise<{
    status: ConnectionStatusView;
    collectsFeedback: boolean;
    degraded: boolean;
    degradedReasons: readonly string[];
    agentReady: boolean;
    steps: readonly { key: string; label: string; complete: boolean; required: boolean; detail: string | null }[];
    guidance: string | null;
  }> => get(`/projects/${projectId}/status`),

  members: (projectId: string): Promise<{ members: readonly Member[] }> =>
    get(`/projects/${projectId}/members`),

  queue: (
    query: TicketQuery,
  ): Promise<{ tickets: readonly TicketSummary[]; total: number; nextCursor: string | null }> =>
    post('/tickets/query', query),

  queueSummary: (projectIds?: readonly string[]): Promise<QueueSummary> =>
    post('/tickets/summary', projectIds === undefined ? {} : { projectIds }),

  board: (projectId: string, siteId?: string | null): Promise<Board> =>
    get<Board>(
      siteId === undefined || siteId === null
        ? `/tickets/board/${projectId}`
        : `/tickets/board/${projectId}?siteId=${encodeURIComponent(siteId)}`,
    ),

  ticket: (ticketId: string): Promise<TicketDetail> => get<TicketDetail>(`/tickets/${ticketId}`),

  ticketByKey: (ticketKey: string): Promise<{ id: string; project_id: string }> =>
    get(`/tickets/by-key/${ticketKey}`),

  createTicket: (input: {
    projectId: string;
    type: TicketType;
    title: string;
    priority: TicketPriority;
    rawDescription: string;
    assigneeUserId: string | null;
  }): Promise<{ ticket: TicketDetail['ticket'] }> => post('/tickets', input),

  updateTicket: (
    ticketId: string,
    update: Record<string, unknown>,
  ): Promise<{ ticket: TicketDetail['ticket'] }> => patch(`/tickets/${ticketId}`, update),

  transition: (
    ticketId: string,
    to: WorkflowState,
    reason: string | null,
  ): Promise<{ ticket: TicketDetail['ticket'] }> =>
    post(`/tickets/${ticketId}/transition`, { to, reason }),

  assign: (
    ticketId: string,
    assigneeKind: 'user' | 'agent' | 'unassigned',
    assigneeUserId: string | null,
  ): Promise<{ ticket: TicketDetail['ticket'] }> =>
    post(`/tickets/${ticketId}/assign`, { assigneeKind, assigneeUserId }),

  move: (
    ticketId: string,
    move: { workflowState?: WorkflowState; beforeTicketId: string | null; afterTicketId: string | null },
  ): Promise<{ ticket: TicketSummary }> => post(`/tickets/${ticketId}/move`, move),

  comment: (
    ticketId: string,
    body: string,
    clientVisible: boolean,
  ): Promise<{ comment: Record<string, unknown> }> =>
    post(`/tickets/${ticketId}/comments`, { body, clientVisible }),

  link: (ticketId: string, toTicketId: string, kind: 'blocks' | 'duplicates' | 'child_of') =>
    post(`/tickets/${ticketId}/links`, { toTicketId, kind }),

  unlink: (ticketId: string, linkId: string): Promise<void> =>
    remove(`/tickets/${ticketId}/links/${linkId}`),

  merge: (ticketId: string, canonicalTicketId: string, carryOverDescription: boolean) =>
    post(`/tickets/${ticketId}/merge`, { canonicalTicketId, carryOverDescription }),

  bulk: (
    ticketIds: readonly string[],
    action: Record<string, unknown>,
  ): Promise<{ succeeded: readonly string[]; failed: readonly { ticketId: string; reason: string }[] }> =>
    post('/tickets/bulk', { ticketIds, action }),

  /* Operations. Distinct from `projectStatus`, which answers "is this wired
     up"; this answers "is it going wrong today". */
  projectHealth: (projectId: string): Promise<ProjectHealth> =>
    get<ProjectHealth>(`/projects/${projectId}/health`),

  hotspots: (projectId: string): Promise<readonly Record<string, unknown>[]> =>
    get(`/analytics/${projectId}/hotspots`),
  timings: (projectId: string): Promise<readonly Record<string, unknown>[]> =>
    get(`/analytics/${projectId}/timings`),

  environments: (projectId: string): Promise<{ environments: readonly Site[] }> =>
    get(`/projects/${projectId}/environments`),

  /* Client teams and domains. Global; only a global administrator may change them. */
  clientTeams: (): Promise<{ teams: readonly ClientTeam[] }> => get('/client-teams'),
  createClientTeam: (name: string): Promise<{ team: ClientTeam }> => post('/client-teams', { name }),
  renameClientTeam: (teamId: string, name: string): Promise<void> => patch(`/client-teams/${teamId}`, { name }),
  deleteClientTeam: (teamId: string): Promise<void> => remove(`/client-teams/${teamId}`),
  clientDomains: (): Promise<{ domains: readonly ClientDomain[]; pending: number }> => get('/client-domains'),
  clientDomainPeople: (domain: string): Promise<{ people: readonly ClientDomainPerson[] }> =>
    get(`/client-domains/${encodeURIComponent(domain)}/people`),
  decideClientDomain: (domain: string, decision: ClientDomainDecision): Promise<unknown> =>
    put(`/client-domains/${encodeURIComponent(domain)}`, decision),
  /** Provisioning. Administrators only; the Worker enforces it. */
  createProject: (input: Record<string, unknown>) => post('/projects', input),
  upsertEnvironment: (projectId: string, input: Record<string, unknown>) =>
    put(`/projects/${projectId}/environments`, input),
  install: (projectId: string, environmentId: string) =>
    get(`/projects/${projectId}/install/${environmentId}`),

  /**
   * Change one site, addressed by its id.
   *
   * Distinct from `upsertEnvironment`, which matches on the label and so
   * cannot rename: a new label there adds a second site and leaves the first
   * one running on its own key.
   */
  updateSite: (
    projectId: string,
    siteId: string,
    input: {
      label?: string;
      kind?: Site['kind'];
      originAllowlist?: readonly string[];
      repo?: string | null;
      ownerSlackNames?: readonly string[];
      slackChannel?: string | null;
    },
  ): Promise<{ environment: Site; snippet: string }> =>
    patch(`/projects/${projectId}/environments/${siteId}`, input),

  /* Lifecycle. Global administrators only, enforced by the Worker. */
  archiveProject: (projectId: string): Promise<{ archivedAt: string }> =>
    post(`/projects/${projectId}/archive`),
  unarchiveProject: (projectId: string): Promise<{ archivedAt: null }> =>
    post(`/projects/${projectId}/unarchive`),

  /**
   * Destroy a project. `confirm` must equal its display name.
   *
   * Checked on the server as well, because this dialog is the part a caller
   * can skip and this is the one action with no undo.
   */
  /**
   * Destroy one ticket. `confirm` must equal its key.
   *
   * Distinct from the soft delete elsewhere: that keeps the row, which is what
   * you want for a duplicate. This is for a report that should not exist.
   */
  purgeTicket: (
    ticketId: string,
    confirm: string,
  ): Promise<{ deleted: boolean; ticketKey: string }> =>
    call(`/tickets/${ticketId}`, { method: 'DELETE', body: JSON.stringify({ confirm }) }),

  /**
   * Destroy every ticket on a project, keeping the project.
   *
   * The sites and keys survive, so a host application keeps working against
   * the same key afterwards — the difference between this and deleting the
   * project and starting again.
   */
  purgeProjectTickets: (
    projectId: string,
    confirm: string,
  ): Promise<{ deleted: boolean; removed: number }> =>
    call(`/tickets/project/${projectId}/all`, {
      method: 'DELETE',
      body: JSON.stringify({ confirm }),
    }),

  /** Everyone with a login, staff and clients alike. */
  listUsers: (options: {
    search?: string;
    role?: string;
    includeInactive?: boolean;
  } = {}): Promise<{
    users: {
      id: string;
      email: string;
      displayName: string;
      globalRole: 'admin' | 'triager' | 'read_only' | 'client_viewer';
      active: boolean;
      createdAt: string;
      updatedAt: string;
    }[];
  }> => {
    const params = new URLSearchParams();
    if (options.search !== undefined && options.search.length > 0) params.set('search', options.search);
    if (options.role !== undefined && options.role.length > 0) params.set('role', options.role);
    if (options.includeInactive === true) params.set('includeInactive', 'true');
    const query = params.toString();
    return call(`/users${query.length > 0 ? `?${query}` : ''}`);
  },

  updateUserRole: (
    userId: string,
    globalRole: string,
  ): Promise<{ user: { id: string; globalRole: string }; changed: boolean }> =>
    call(`/users/${userId}/role`, { method: 'PATCH', body: JSON.stringify({ globalRole }) }),

  deleteProject: (
    projectId: string,
    confirm: string,
  ): Promise<{
    deleted: {
      projectId: string;
      displayName: string;
      tickets: number;
      submissions: number;
      sites: number;
      attachments: number;
    };
  }> => call(`/projects/${projectId}`, { method: 'DELETE', body: JSON.stringify({ confirm }) }),
};
